import React, { FC, useEffect, useMemo, useState } from 'react';
import { Menu } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { Spin } from 'common/components';
import { ERoute } from 'common/const';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import {
  SettingsAccountContractsTab,
  SettingsAccountIntegrationsTab,
  SettingsAccountLegalEntityListTab,
  SettingsAccountPersonalLimitsTab,
  SettingsAccountName,
  SettingsAccountStockListTab,
  SettingsAccountUserListTab,
  SettingsAccountAddressesTab,
} from 'entities/Settings/components';
import { ESettingsMenuTab, ESettingsTab } from 'entities/Settings/Settings.enum';
import { getAccountSettingsMenuItems } from 'entities/Settings/Settings.helper';
import { getAccountById, selectAccount, selectIsAccountLoading, updateAccount } from 'entities/Account/Account.slice';
import { IUpdateAccountPayload } from 'entities/Account/Account.models';
import { selectCurrentUser } from 'entities/User/User.slice';

interface IComponentProps {
  isSeller?: boolean;
}

export const SettingsAccount: FC<IComponentProps> = ({ isSeller }) => {
  const dispatch = useAppDispatch();
  // account
  const account = useAppSelector(selectAccount);
  const isAccountLoading = useAppSelector(selectIsAccountLoading);
  // user
  const currentUser = useAppSelector(selectCurrentUser);

  const [fetchLoading, setFetchLoading] = useState<boolean>(true);
  const { menu } = useParams();
  const navigate = useNavigate();

  const accountId = currentUser?.account?.id;

  const handleUpdateAccount = async (payload: IUpdateAccountPayload): Promise<void> => {
    await dispatch(updateAccount(payload));
  };

  const header = useMemo(() => {
    return (
      <>
        <div className="text-body color-dark-grey mb-6">Настройки аккаунта</div>

        <SettingsAccountName account={account} loading={isAccountLoading} onAccountUpdate={handleUpdateAccount} />

        <Menu
          items={getAccountSettingsMenuItems(isSeller)}
          selectedKeys={[menu ? menu : isSeller ? ESettingsMenuTab.UserList : ESettingsMenuTab.Contracts]}
          onClick={({ key }) => navigate(`${ERoute.Settings}/${ESettingsTab.Account}/${key}`)}
          mode="horizontal"
        />
      </>
    );
  }, [account, isAccountLoading, menu]);

  const renderContent = () => {
    switch (menu) {
      case ESettingsMenuTab.UserList: {
        return <SettingsAccountUserListTab header={header} account={account} isSeller={isSeller} />;
      }
      case ESettingsMenuTab.LegalEntityList: {
        return <SettingsAccountLegalEntityListTab header={header} />;
      }
      case ESettingsMenuTab.StockList: {
        return <SettingsAccountStockListTab header={header} />;
      }
      case ESettingsMenuTab.Integrations: {
        return <SettingsAccountIntegrationsTab header={header} />;
      }
      case ESettingsMenuTab.PersonalLimits: {
        return <SettingsAccountPersonalLimitsTab header={header} account={account} />;
      }
      case ESettingsMenuTab.AddressList: {
        return <SettingsAccountAddressesTab header={header} account={account} />;
      }
      default: {
        return <SettingsAccountContractsTab header={header} account={account} onAccountUpdate={handleUpdateAccount} />;
      }
    }
  };

  useEffect(() => {
    const fetch = async () => {
      if (accountId) {
        await dispatch(getAccountById(accountId));
      }

      setFetchLoading(false);
    };

    fetch();
  }, [accountId]);

  if (fetchLoading) {
    return (
      <div className="relative h-full flex align-center justify-center flex-1">
        <Spin />
      </div>
    );
  }

  return <div className="relative h-full flex direction-column flex-1 account-settings">{renderContent()}</div>;
};
